import Link from "next/link";
import React from "react";
import { cn } from "@punchup/utils/cn";

type PolicyLink = {
  text: string;
  href: string;
};

type PolicyTemplate = {
  topText?: string;
  mainText: string;
  links: PolicyLink[];
  extraContent?: {
    text: string;
    link?: PolicyLink;
  };
};

type SinglePolicyType =
  | "default"
  | "comedian"
  | "comedian-email"
  | "comedian-phone"
  | "hidden";

type PolicyType = SinglePolicyType | [SinglePolicyType, SinglePolicyType];

const SMS_BLOG_LINK =
  "https://blog.Punchup.live/about-us/#:~:text=I%20signed%20up%20via%20Email%20or%20SMS%2C%20are%20you%20guys%20going%20to%20send%20me%20messages%3F";

type Props = {
  className?: string;
  type?: PolicyType;
  comedian?: string;
};

/**
 * Renders terms of service and privacy policy links with configurable templates.
 * Supports responsive desktop/mobile configurations via a [desktop, mobile] tuple type.
 *
 * Available types:
 * - "default" — generic ToS + Privacy agreement
 * - "comedian" — combined email/SMS consent with comedian name
 * - "comedian-email" — email-specific consent acknowledgement
 * - "comedian-phone" — SMS-specific consent with recurring message disclosure
 * - "hidden" — renders nothing (useful in one half of a responsive tuple)
 *
 * @param className - Optional CSS classes to apply to the container
 * @param type - Template type or a [desktop, mobile] tuple for responsive behavior
 * @param comedian - Optional comedian name to integrate into the message
 */

function PolicyLinks() {
  return (
    <>
      <Link
        href="/privacy"
        className="whitespace-nowrap text-primary font-medium"
      >
        Privacy Policy
      </Link>
      <span> and agree to our </span>
      <Link href="/tos" className="whitespace-nowrap text-primary font-medium">
        Terms of Service
      </Link>
    </>
  );
}

function renderContent(type: SinglePolicyType, comedian?: string) {
  if (type === "hidden") return null;

  if (type === "default") {
    return (
      <p className="font-light text-secondary-blue">
        By continuing, you agree to Punchup&apos;s{" "}
        <Link
          href="/tos"
          className="whitespace-nowrap text-primary font-medium"
        >
          Terms of Service
        </Link>{" "}
        and{" "}
        <Link
          href="/privacy"
          className="whitespace-nowrap text-primary font-medium"
        >
          Privacy Policy
        </Link>
      </p>
    );
  }

  if (type === "comedian-email") {
    return (
      <p className="font-light text-secondary-blue">
        By providing your email, you acknowledge that you&apos;ve read our{" "}
        <PolicyLinks />.
      </p>
    );
  }

  if (type === "comedian-phone") {
    return (
      <p className="font-light text-secondary-blue">
        By providing your phone number, you agree to receive recurring SMS
        messages from Punchup on behalf of artists and you acknowledge that
        you&apos;ve read our <PolicyLinks />. You can learn more about how we
        use SMS{" "}
        <Link
          href={SMS_BLOG_LINK}
          className="whitespace-nowrap text-primary font-medium"
        >
          here
        </Link>
        .
      </p>
    );
  }

  // "comedian" — the original combined variant
  return (
    <div>
      <p className="font-light text-secondary-blue">
        By continuing, you agree to receive recurring messages from Punchup on
        behalf of artists to the number or email you provide and to
        Punchup&apos;s{" "}
        <Link
          href="/tos"
          className="whitespace-nowrap text-primary font-medium"
        >
          Terms of Service
        </Link>{" "}
        and{" "}
        <Link
          href="/privacy"
          className="whitespace-nowrap text-primary font-medium"
        >
          Privacy Policy
        </Link>
        . You can learn more about how we use email and sms{" "}
        <Link
          href={SMS_BLOG_LINK}
          className="whitespace-nowrap text-primary font-medium"
        >
          here
        </Link>
      </p>
    </div>
  );
}

export default function PoliciesFooter({
  className,
  type = "default",
  comedian,
}: Props) {
  const [desktopType, mobileType] = Array.isArray(type) ? type : [type, type];

  if (desktopType === "hidden" && mobileType === "hidden") return null;

  return (
    <>
      {desktopType !== "hidden" && (
        <div
          className={cn(
            "text-[11px] w-full max-w-md text-secondary-blue text-center mt-1.5 md:block hidden",
            className
          )}
        >
          {renderContent(desktopType, comedian)}
        </div>
      )}

      {mobileType !== "hidden" && (
        <div
          className={cn(
            "text-[11px] w-full max-w-md text-secondary-blue text-center mt-1.5 block md:hidden",
            className
          )}
        >
          {renderContent(mobileType, comedian)}
        </div>
      )}
    </>
  );
}

export type { PolicyTemplate, PolicyLink, PolicyType, SinglePolicyType };
